import Tooltip from "@mui/material/Tooltip";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { memo, useCallback } from "react";
import CustomButtonV2 from "../../../../../components/CustomButtonV2/CustomButtonV2";
import NewIcon from "../../../../../components/NewIcon/NewIcon";
import { useProfile } from "../../../../../contexts/ProfileContext.tsx";
import IconButton from "../../../../../core/components/IconButton.tsx";
import Separator from "../../../../../core/components/Separator.tsx";
import SelectInputField from "../../../../../core/data-entry/SelectInputField.tsx";
import { valorMask } from "../../../../../regex/valorMask";
import CadastroViagemSectionTitle from "../../components/CadastroViagemSectionTitle.tsx";

const toNumber = (val) =>
	parseFloat(
		String(val ?? "0")
			.replace(/\./g, "")
			.replace(",", ".")
	) || 0;

function Entregas({
	entrega,
	pedidoValido,
	pedidoOptions,
	handleInputChange,
	handlePedidoEntregaChange,
	setOpenCadastroPedido,
	setIdAtual,
	setDisable,
	setEntregas,
	taxaIcms,
	clienteOptions,
	handleClienteInputChange,
	destinatarioValido,
	setOpenCadastroCliente,
	disabledFreteFiscalEntrega,
	setValues,
	removeEntrega,
	values,
	adicionarEntrega,
	setValuesSelect,
	setValidator,
	pedido,
	setPedidoOptions,
	icmsBackup,
}) {
	const { contextoEmbarque } = useProfile();

	const clearPedidoEntrega = (index) => {
		if (entrega.length > 0) {
			const list = [...entrega];
			list[index].idEntrega = 0;
			list[index].embarqueId = 0;
			list[index].pedidoId = 0;
			list[index].destinarioId = 0;
			list[index].classificacao = 0;
			list[index].freteMotorista = 0;
			list[index].freteFiscal = 0;
			list[index].icms = 0;
			list[index].pedagio = 0;
			list[index].km = 0;
			list[index].embarcador = "";
			list[index].cliente = "";
			list[index].destinatarioTitle = "";
			list[index].cte = "";
			list[index].nomePedido = "";
			setEntregas(list);
		}
	};

	const clearDestinatario = () => {
		setValuesSelect((current) => ({ ...current, destinatario: "" }));
		setValues((current) => ({ ...current, destinatario: "" }));
		setValues((current) => ({ ...current, destinatarioId: null }));
		setValidator((current) => ({ ...current, validatorDestinatario: "false" }));
		setValuesSelect((current) => ({ ...current, destinatarioLabel: "" }));
	};

	const handlePedidoInputChange = useCallback(
		(inputValue) => {
			const normalizedInput = inputValue.target.value.trim().toLowerCase();

			if (normalizedInput === "") {
				setPedidoOptions([]);
				return;
			}

			const filteredOptions = pedido
				.filter((pedido) => {
					const tipoRota = pedido.tipoRota?.toLowerCase() || "";
					const cidadeDestino =
						pedido.rota?.cidadeDestinoRota.cidade?.toLowerCase() || "";
					const cidadeOrigem =
						pedido.rota?.cidadeOrigemRota.cidade?.toLowerCase() || "";
					const pedidoNumero = pedido.pedido?.toString() || "";

					return (
						tipoRota.includes(normalizedInput) ||
						cidadeDestino.includes(normalizedInput) ||
						cidadeOrigem.includes(normalizedInput) ||
						pedidoNumero.includes(normalizedInput)
					);
				})
				.sort((a, b) => a.pedido - b.pedido)
				.slice(0, 100)
				.map((data) => ({
					title: `${data.pedido} - ${data.tipoRota} @ ${data.rota.cidadeDestinoRota.cidade} - ${data.rota.cidadeDestinoRota.abreviacao} - ${data.rota.cidadeOrigemRota.cidade} - ${data.rota.cidadeOrigemRota.abreviacao}`,
					obj: data,
				}));

			setPedidoOptions(filteredOptions);
		},
		[pedido, setPedidoOptions]
	);

	const calcularIcmsEntrega = useCallback(
		(ent) => {
			if (contextoEmbarque === "frota") {
				setValues((c) => ({ ...c, icms: "0,00" }));
				return;
			}

			const freteFiscal = toNumber(ent.freteFiscal);
			const pedagio = ent.diPedagio ? toNumber(ent.pedagio) : 0;
			const classificacao = ent.diClassificacao
				? toNumber(ent.classificacao)
				: 0;

			const somatoria = freteFiscal + pedagio + classificacao;
			const taxa = (somatoria / (1 - taxaIcms)) * taxaIcms;
			const taxaFormatada = Number.isFinite(taxa)
				? taxa.toFixed(2).replace(".", ",")
				: "0,00";
			return taxaFormatada;
		},
		[contextoEmbarque, taxaIcms, setValues]
	);

	const atualizarEntregaValores = (index, patch) => {
		const list = [...entrega];
		list[index] = { ...list[index], ...patch };

		list[index].icms = calcularIcmsEntrega(list[index]);

		setEntregas(list);
	};
	
	return (
		<>
			<CadastroViagemSectionTitle title="Entregas:" />
			<div className="container-select-emb">
				<LayoutGroup>
					<AnimatePresence>
						{entrega &&
							entrega.map((e, index) => {
								return (
									<motion.div
										key={e.localKey ?? e.idEntrega ?? `entrega-${index}`}
										data-localkey={
											e.localKey ?? e.idEntrega ?? `entrega-${index}`
										}
										layoutId={e.localKey ?? e.idEntrega ?? `entrega-${index}`}
										layout
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: "auto", opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ duration: 0.3 }}
									>
										<div className="entrega-embarque-container">
											<h5>Entrega {index + 1}</h5>
											<Separator size="lg" />
											<div className="entrega-row">
												<div className="entrega-row">
													<div
														className={
															pedidoValido ? "coluna-emb" : "coluna-emb-erro"
														}
													>
														<SelectInputField
															label="Pedido"
															placeholder="Digite para buscar."
															options={pedidoOptions}
															value={entrega[index].nomePedido || ""}
															onInputChange={handlePedidoInputChange}
															name="pedidoId"
															onChange={(e) => {
																handleInputChange(
																	{
																		target: {
																			name: "pedidoId",
																			value: e.obj.idPedido,
																		},
																	},
																	index
																);
																handleInputChange(
																	{
																		target: {
																			name: "nomePedido",
																			value: e.title,
																		},
																	},
																	index
																);
																handlePedidoEntregaChange(e, index);
															}}
															error={!pedidoValido}
															clearIcon={true}
															onClear={() => clearPedidoEntrega(index)}
														/>
														<NewIcon
															onClick={() => {
																setOpenCadastroPedido(true);
																setIdAtual(null);
																setDisable(false);
															}}
														/>
													</div>
													<div className="coluna-emb">
														<div>
															<p
																className="input-label-emb"
																htmlFor="descricao"
															>
																Classificação:
															</p>
															<input
																type="text"
																className="input-emb"
																placeholder="0,00"
																style={{ width: "100px" }}
																onChange={(e) => {
																	const list = [...entrega];
																	list[index].classificacao = e.target.value;
																	setEntregas(list);
																	if (
																		taxaIcms === 0 ||
																		taxaIcms === 0.0 ||
																		taxaIcms === 0.0 ||
																		taxaIcms === "0,00" ||
																		taxaIcms === "0,0"
																	) {
																		const list = [...entrega];
																		list[index].icms = 0;
																		setEntregas(list);
																	} else {
																		if (
																			!taxaIcms &&
																			contextoEmbarque !== "frota"
																		) {
																			const list = [...entrega];
																			list[index].icms = parseFloat(
																				icmsBackup?.icms.replace(",", ".")
																			);
																			setEntregas(list);
																		}
																	}
																}}
																value={valorMask(entrega[index].classificacao)}
															/>
														</div>
													</div>
													<div className="coluna-emb">
														<div>
															<p
																className="input-label-emb"
																htmlFor="descricao"
															>
																Cliente:
															</p>
															<input
																type="text"
																className="input-emb"
																style={{ width: "132px" }}
																value={entrega[index].cliente}
																disabled={true}
															/>
														</div>
													</div>
													<div className="coluna-emb">
														<div>
															<p
																className="input-label-emb"
																htmlFor="descricao"
															>
																Embarcador:
															</p>
															<input
																type="text"
																className="input-emb"
																style={{ width: "132px" }}
																value={entrega[index].embarcador}
																disabled={true}
															/>
														</div>
													</div>
													<div className="coluna-emb">
														<SelectInputField
															label="Destinatário"
															options={clienteOptions}
															value={entrega[index].destinatarioTitle}
															onInputChange={handleClienteInputChange}
															onClear={clearDestinatario}
															onChange={(selectedOption) => {
																const list = [...entrega];
																list[index].destinarioId =
																	selectedOption.obj.idCliente;
																list[index].destinatarioTitle = `${
																	selectedOption.obj.nomeFantasia
																} | ${
																	selectedOption.obj.apelido
																		? selectedOption.obj.apelido
																		: "Favor, cadastrar apelido de Cliente"
																}`;
																setEntregas(list);
															}}
															error={!destinatarioValido}
														/>
														<NewIcon
															onClick={() => {
																setOpenCadastroCliente(true);
																setIdAtual(null);
																setDisable(false);
															}}
														/>
													</div>
												</div>
												<div className="entrega-row">
													<div className="coluna-2-emb">
														<div className="coluna-emb">
															<div>
																<p
																	className="input-label-emb"
																	htmlFor="descricao"
																>
																	Km:
																</p>
																<input
																	type="text"
																	className="input-emb"
																	style={{ width: "152px" }}
																	value={entrega[index].km}
																	disabled={true}
																/>
															</div>
														</div>
														<div className="coluna-emb">
															<div>
																<p
																	className="input-label-emb"
																	htmlFor="descricao"
																>
																	Frete Motorista:
																</p>
																<input
																	type="text"
																	className="input-emb"
																	style={{ width: "126.5px" }}
																	onChange={(e) => {
																		const list = [...entrega];
																		list[index].freteMotorista = e.target.value;
																		setEntregas(list);
																	}}
																	value={valorMask(
																		entrega[index].freteMotorista
																	)}
																/>
															</div>
														</div>
														<div className="coluna-emb">
															<div>
																<p
																	className="input-label-emb"
																	htmlFor="descricao"
																>
																	Pedágio:
																</p>
																<input
																	type="text"
																	className="input-emb"
																	style={{ width: "126.5px" }}
																	onChange={(e) => {
																		const list = [...entrega];
																		list[index].pedagio = e.target.value;
																		setEntregas(list);
																		if (
																			taxaIcms === 0 ||
																			taxaIcms === 0.0 ||
																			taxaIcms === 0.0 ||
																			taxaIcms === "0,00" ||
																			taxaIcms === "0,0"
																		) {
																			setValues((current) => ({
																				...current,
																				icms: "0",
																			}));
																		} else {
																			if (
																				taxaIcms === undefined &&
																				contextoEmbarque !== "frota"
																			) {
																				setValues((current) => ({
																					...current,
																					icms: icmsBackup?.icms,
																				}));
																			}
																		}
																	}}
																	value={
																		!!valorMask(entrega[index].pedagio)
																			? valorMask(entrega[index].pedagio)
																			: 0
																	}
																/>
															</div>
														</div>
														<div className="coluna-emb">
															<div>
																<p
																	className="input-label-emb"
																	htmlFor="descricao"
																>
																	Frete Fiscal:
																</p>
																<input
																	type="text"
																	className="input-emb"
																	style={{ width: "126.5px" }}
																	onChange={(e) => {
																		atualizarEntregaValores(index, {
																			freteFiscal: e.target.value,
																		});
																	}}
																	value={valorMask(entrega[index].freteFiscal)}
																	disabled={disabledFreteFiscalEntrega[index]}
																/>
															</div>
														</div>
														<div className="coluna-emb">
															<div>
																<p
																	className="input-label-emb"
																	htmlFor="descricao"
																>
																	ICMS:
																</p>
																<input
																	type="text"
																	className="input-emb"
																	style={{ width: "126.5px" }}
																	onChange={(e) => {
																		const list = [...entrega];
																		list[index].icms = e;
																		setEntregas(list);
																	}}
																	value={entrega[index].icms}
																	disabled={true}
																/>
															</div>
														</div>
														<div className="coluna-emb">
															<div>
																<p
																	className="input-label-emb"
																	htmlFor="descricao"
																>
																	CTE:
																</p>
																<input
																	name="cte"
																	maxLength="5"
																	placeholder="00000"
																	type="text"
																	className="input-emb"
																	style={{ width: "190px" }}
																	onChange={(e) => {
																		let valor = e.target.value;
																		valor = valor.replace(/\D/g, "");
																		const list = [...entrega];
																		list[index].cte = valor;
																		setEntregas(list);
																	}}
																	value={e.cte}
																/>
															</div>
														</div>
													</div>
												</div>
											</div>
											<div className="grid-item-entrega">
												<Tooltip title="Remover Entrega">
													<IconButton
														icon="Trash2"
														size="xl"
														theme="dark"
														backgroundColor="#fefefe"
														hoverColor="#ccc"
														onClick={() => {
															removeEntrega(index);
														}}
													/>
												</Tooltip>
											</div>
										</div>
										<Separator size="lg" />
									</motion.div>
								);
							})}
					</AnimatePresence>
				</LayoutGroup>
			</div>

			<div className="container-adicionar-entrega">
				<CustomButtonV2
					classCss="custom-button-entrega"
					classText="button-texto-entrega"
					titulo="Adicionar Entrega"
					cor="#0946B8"
					valor={values}
					onClick={() => {
						adicionarEntrega();
					}}
				/>
			</div>
		</>
	);
}

export default memo(Entregas);
